import { COIN_LIST, COIN_MAP } from './coinList';
import { getFiatToEurRate } from './fxConvert';
import { safeLocalSet } from './lsCache';
export { COIN_LIST, COIN_MAP };

// ── Historical-Daten ────────────────────────────────────────────────
// Drei Layer:
//   1) In-Memory-Cache  — 60 min TTL (verhindert Refetch bei Re-Render)
//   2) localStorage     — 24 h TTL (überlebt Tab-Reload, Stale-while-Error)
//   3) Inflight-Dedup   — gleichzeitige Calls auf gleichen Key sharen einen fetch
// Hinzu: 5 min Error-Cooldown + Stale-Fallback bei Fehler.

const historicalMem = new Map();      // key -> { data, fetchedAt }
const inflightHist = new Map();       // key -> Promise<data>
const errorCooldown = {};             // key -> ts of last error

const HIST_MEM_TTL = 60 * 60 * 1000;       // 60 min
const HIST_PERSIST_TTL = 24 * 60 * 60 * 1000; // 24 h
const HIST_ERROR_COOLDOWN = 5 * 60 * 1000;   // 5 min
const HIST_PERSIST_PREFIX = 'redapp_cg_hist_v1__';

function persistKey(key) { return HIST_PERSIST_PREFIX + key; }

function readPersisted(key) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(persistKey(key));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.fetchedAt !== 'number' || !obj.data) return null;
    return obj;
  } catch { return null; }
}

function writePersisted(key, data) {
  // fetchedAt ZUERST: lsCache liest den Zeitstempel billig aus dem Anfang des Strings.
  // safeLocalSet erzwingt das Cache-Budget (verhindert localStorage-Überlauf → Firestore-Crash).
  safeLocalSet(persistKey(key), JSON.stringify({ fetchedAt: Date.now(), data }));
}

// SPL-Token (coinId `spl:<mint>`) stehen nicht bei CoinGecko → Verlauf kommt aus
// Jupiters Chart-API (datapi). days → Intervall/Kerzenzahl (bestätigte Tokens:
// 15_MINUTE/4_HOUR/1_DAY). USD→EUR (Jupiter ist USD), gleiche Form wie CoinGecko:
// [[ts_ms, price], …].
function jupChartParams(days) {
  if (days <= 2)  return { interval: '15_MINUTE', candles: Math.min(192, Math.max(8, Math.round(days * 96))) };
  if (days <= 14) return { interval: '4_HOUR',    candles: Math.min(120, Math.max(8, Math.round(days * 6))) };
  return { interval: '1_DAY', candles: Math.min(400, Math.max(8, Math.round(days))) };
}

async function fetchSplHistorical(mint, days, cur) {
  const { interval, candles } = jupChartParams(days);
  const to = Date.now();
  const res = await fetch(`https://datapi.jup.ag/v2/charts/${mint}?interval=${interval}&candles=${candles}&to=${to}`);
  if (!res.ok) throw new Error(`Jupiter chart ${res.status}`);
  const j = await res.json();
  const rows = (j?.candles || []).filter((c) => Number.isFinite(Number(c?.close)) && Number(c?.close) > 0);
  if (!rows.length) throw new Error('Jupiter chart leer');
  let rate = 1;
  if (cur === 'eur') {
    try { rate = (await getFiatToEurRate('USD', new Date().toISOString().slice(0, 10))) || 1; } catch { rate = 1; }
  }
  return rows.map((c) => [Number(c.time) * 1000, Number(c.close) * rate]);
}

export async function fetchHistoricalData(coinId, days, currency = 'eur') {
  const cur = (currency || 'eur').toLowerCase();
  const key = `${coinId}_${days}_${cur}`;
  const now = Date.now();
  const isSpl = typeof coinId === 'string' && coinId.startsWith('spl:');

  // 1) Memory-Cache frisch genug?
  const mem = historicalMem.get(key);
  if (mem && now - mem.fetchedAt < HIST_MEM_TTL) {
    return mem.data;
  }

  // 2) Inflight: gleiche Request läuft schon → andocken
  const running = inflightHist.get(key);
  if (running) return running;

  // 3) Error-Cooldown: lieber stale Cache liefern als Netz hämmern
  const lastErr = errorCooldown[key];
  if (lastErr && now - lastErr < HIST_ERROR_COOLDOWN) {
    const stale = mem || readPersisted(key);
    if (stale) {
      if (!mem) historicalMem.set(key, stale);
      return stale.data;
    }
    throw new Error('Kurzzeitige Pause nach Fehler – bitte warten');
  }

  // 4) Persistierter Cache frisch genug → ohne Netz hochziehen
  const persisted = readPersisted(key);
  if (persisted && now - persisted.fetchedAt < HIST_MEM_TTL) {
    historicalMem.set(key, persisted);
    return persisted.data;
  }

  // 5) Netz-Fetch (mit Inflight-Dedup) — SPL via Jupiter, sonst CoinGecko.
  const url = `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=${cur}&days=${days}`;
  const promise = (async () => {
    try {
      let prices;
      if (isSpl) {
        prices = await fetchSplHistorical(coinId.slice(4), days, cur);
      } else {
        const res = await fetch(url);
        if (!res.ok) {
          errorCooldown[key] = Date.now();
          throw new Error(`CoinGecko ${res.status}`);
        }
        const json = await res.json();
        prices = json.prices;
      }
      delete errorCooldown[key];
      const entry = { data: prices, fetchedAt: Date.now() };
      historicalMem.set(key, entry);
      writePersisted(key, prices);
      return prices;
    } catch (err) {
      errorCooldown[key] = Date.now();
      // Stale-while-error: lieber alter persistierter Stand als gar nichts
      const fallback = persisted || mem;
      if (fallback && now - fallback.fetchedAt < HIST_PERSIST_TTL) {
        historicalMem.set(key, fallback);
        return fallback.data;
      }
      throw err;
    } finally {
      inflightHist.delete(key);
    }
  })();

  inflightHist.set(key, promise);
  return promise;
}

// ── Historischer Tages-Preis (für externe Transfers ohne EUR-Wert) ──
// Nutzt /coins/{id}/history?date=DD-MM-YYYY → liefert Schlusspreis des Tages.
// Historische Werte sind immutable → langer Cache (30 Tage) und keine Stale-Logik.

const histDayMem = new Map();      // key -> price (number) | null (not-found)
const inflightHistDay = new Map(); // key -> Promise<price>
const HIST_DAY_PERSIST_PREFIX = 'redapp_cg_hist_day_v1__';
const HIST_DAY_TTL = 30 * 24 * 60 * 60 * 1000;

function histDayKey(coinId, date, cur) {
  return `${coinId}_${date}_${cur}`;
}

function readHistDayPersisted(key) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(HIST_DAY_PERSIST_PREFIX + key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.fetchedAt !== 'number') return null;
    if (Date.now() - obj.fetchedAt > HIST_DAY_TTL) return null;
    return obj.price; // number oder null (für "kein Preis verfügbar")
  } catch { return null; }
}

function writeHistDayPersisted(key, price) {
  // fetchedAt vorn halten (price ist klein) → lsCache findet den Zeitstempel im Head.
  safeLocalSet(HIST_DAY_PERSIST_PREFIX + key, JSON.stringify({ fetchedAt: Date.now(), price }));
}

// Native iOS: relative /api/... geht auf capacitor://localhost/api/... (kein Server).
// Runtime-Check statt Build-Env, damit Web-Bundle unverändert bleibt.
const API_BASE = (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.())
  ? 'https://app.redcrypto.de'
  : (process.env.REACT_APP_API_BASE || '');

// Cache-Version für den historischen Preis-Proxy. Hochzählen, wenn alte
// gecachte Antworten (Browser/Edge) invalidiert werden müssen — z.B. nachdem
// neue Fallback-Preisquellen (Binance/CryptoCompare) ergänzt wurden, damit
// vorher als „null" gecachte Tage neu geholt werden.
const PRICE_CACHE_VERSION = 3;

/**
 * Holt den historischen Tagespreis über den Backend-Proxy `/api/coin-price-history`.
 * Der Proxy nutzt einen CoinGecko-Demo/Pro-Key und liefert daher auch Preise für
 * TXs, die >365 Tage zurückliegen.
 *
 * Liefert die Auth-Header mit, damit der guard.js-Middleware-Stack passiert wird.
 */
async function fetchPriceViaProxy(coinId, date, currency, sym) {
  try {
    const { authHeader } = await import('../services/authHeader');
    const auth = await authHeader();
    // sym (Coin-Symbol, z.B. HBAR) ermöglicht die Binance/CryptoCompare-Fallbacks
    // im Proxy für Tage >365 (CoinGecko-Demo-Limit). coinId allein reicht nicht.
    const symQ = sym ? `&sym=${encodeURIComponent(sym)}` : '';
    // cv = Cache-Version. Hochzählen invalidiert alte Browser-/Edge-Cache-Einträge
    // (z.B. „null" die gecached wurden, BEVOR die Fallback-Quellen existierten) →
    // neuer Cache-Key, frischer Fetch. Bei künftigen Quellen-Änderungen erhöhen.
    const url = `${API_BASE}/api/coin-price-history?coin=${encodeURIComponent(coinId)}&date=${encodeURIComponent(date)}&currency=${encodeURIComponent(currency)}${symQ}&cv=${PRICE_CACHE_VERSION}`;
    const res = await fetch(url, { headers: { ...auth } });
    if (!res.ok) {
      // 503 = kein API-Key konfiguriert → still auf Fallback gehen.
      // 404 = Coin/Datum unbekannt → hart null cachen, kein Fallback.
      // Sonstige Fehler → Fallback versuchen.
      return { ok: false, status: res.status };
    }
    const json = await res.json();
    if (typeof json?.price === 'number' && Number.isFinite(json.price)) {
      return { ok: true, price: json.price };
    }
    return { ok: false, status: 404 };
  } catch {
    return { ok: false, status: 0 };
  }
}

/**
 * Liefert den historischen Tagespreis eines Coins (Schluss des angeforderten Tages
 * in UTC). Versucht zuerst den Backend-Proxy (CoinGecko-Key-basiert, beliebig alt),
 * fällt zurück auf den öffentlichen `/market_chart`-Endpoint (nur ≤365d).
 *
 * Cache: in-memory + localStorage. Historische Tageswerte sind immutable → 30 Tage TTL.
 *
 * @param {string} coinId    CoinGecko-ID (z.B. 'hedera-hashgraph')
 * @param {string} date      ISO-Datum 'YYYY-MM-DD'
 * @param {string} currency  Default 'eur'
 * @returns {Promise<number|null>}  Einheitspreis am Tag, oder null wenn nicht ermittelbar
 */
export async function getHistoricalPriceAt(coinId, date, currency = 'eur', sym) {
  if (!coinId || !date || typeof date !== 'string' || date.length < 10) return null;
  const cur = (currency || 'eur').toLowerCase();
  const dayKey = date.slice(0, 10);
  const key = histDayKey(coinId, dayKey, cur);

  if (histDayMem.has(key)) return histDayMem.get(key);

  const persisted = readHistDayPersisted(key);
  if (persisted !== null) {
    histDayMem.set(key, persisted);
    return persisted;
  }

  const inflight = inflightHistDay.get(key);
  if (inflight) return inflight;

  const targetMs = Date.UTC(
    Number(dayKey.slice(0, 4)),
    Number(dayKey.slice(5, 7)) - 1,
    Number(dayKey.slice(8, 10)),
    12, 0, 0
  );
  if (!Number.isFinite(targetMs) || targetMs > Date.now() + 86400000) {
    histDayMem.set(key, null);
    writeHistDayPersisted(key, null);
    return null;
  }
  const ageDays = Math.ceil((Date.now() - targetMs) / 86400000);

  const promise = (async () => {
    try {
      // 1) Backend-Proxy bevorzugen — kann beliebig alte Daten liefern.
      const proxy = await fetchPriceViaProxy(coinId, dayKey, cur, sym);
      if (proxy.ok) {
        histDayMem.set(key, proxy.price);
        writeHistDayPersisted(key, proxy.price);
        return proxy.price;
      }

      // Bei 404 (Coin/Datum unbekannt) ist Fallback zwecklos → hart null.
      // Bei 503 (kein Key konfiguriert) oder Netzwerkfehler → Fallback versuchen.
      const isHardMiss = proxy.status === 404;
      if (isHardMiss) {
        histDayMem.set(key, null);
        writeHistDayPersisted(key, null);
        return null;
      }

      // 2) Fallback: öffentlicher market_chart — nur sinnvoll für ≤365 Tage.
      if (ageDays < 0 || ageDays > 365) {
        histDayMem.set(key, null);
        writeHistDayPersisted(key, null);
        return null;
      }
      const days = Math.min(365, Math.max(2, ageDays + 1));
      const url = `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=${cur}&days=${days}`;
      const res = await fetch(url);
      if (!res.ok) {
        histDayMem.set(key, null);
        return null;
      }
      const json = await res.json();
      const prices = Array.isArray(json?.prices) ? json.prices : [];
      let best = null;
      for (const point of prices) {
        if (!Array.isArray(point) || point.length < 2) continue;
        const ts = point[0];
        const p  = point[1];
        if (typeof ts !== 'number' || typeof p !== 'number' || !Number.isFinite(p)) continue;
        const distance = Math.abs(ts - targetMs);
        if (!best || distance < best.distance) best = { distance, price: p };
      }
      const value = (best && best.distance <= 36 * 3600 * 1000) ? best.price : null;
      histDayMem.set(key, value);
      writeHistDayPersisted(key, value);
      return value;
    } catch {
      return null;
    } finally {
      inflightHistDay.delete(key);
    }
  })();

  inflightHistDay.set(key, promise);
  return promise;
}

export function clearHistoricalCache(coinId, days, currency) {
  const clearKey = (key) => {
    historicalMem.delete(key);
    delete errorCooldown[key];
    inflightHist.delete(key);
    if (typeof localStorage !== 'undefined') {
      try { localStorage.removeItem(persistKey(key)); } catch {}
    }
  };
  if (currency) {
    clearKey(`${coinId}_${days}_${currency.toLowerCase()}`);
    return;
  }
  // ohne currency: alle Varianten
  for (const key of Array.from(historicalMem.keys())) {
    if (key.startsWith(`${coinId}_${days}_`)) clearKey(key);
  }
  Object.keys(errorCooldown).forEach(k => {
    if (k.startsWith(`${coinId}_${days}_`)) clearKey(k);
  });
}

// ── Live-Markets (Preise + Icons) ───────────────────────────────────

const marketCache = new Map(); // key: `${cur}|${id}` → { price, chg, image, fetchedAt }
const MARKET_TTL = 60 * 1000;
const MARKET_ERROR_COOLDOWN = 90 * 1000;
let marketErrorUntil = 0;
// reqKey → { promise }. Dedupliziert parallele Calls auf DIESELBEN IDs. Bewusst
// KEIN globaler Single-Slot mit Abort mehr: ein Single-Coin-/Screen-Fetch durfte
// sonst den großen loadMarket-Fetch abbrechen → „alle Preise 0 €"-Regression.
const inflight = new Map();

function readMarketCache(ids, cur) {
  const out = {};
  const missing = [];
  const now = Date.now();
  for (const id of ids) {
    const entry = marketCache.get(`${cur}|${id}`);
    if (entry) {
      out[id] = { price: entry.price, chg: entry.chg, chg7d: entry.chg7d, mcap: entry.mcap, image: entry.image, spark: entry.spark };
      if (now - entry.fetchedAt >= MARKET_TTL) missing.push(id);
    } else {
      missing.push(id);
    }
  }
  return { out, missing };
}

export async function fetchMarketData(ids = [], currency = 'eur', opts = {}) {
  if (!ids.length) return {};
  const cur = (currency || 'eur').toLowerCase();
  // force = Pull-to-Refresh: 60s-Memory-Cache + Error-Cooldown für diese IDs
  // verwerfen, damit garantiert frisch beim Proxy/CoinGecko geholt wird.
  if (opts && opts.force) {
    for (const id of ids) marketCache.delete(`${cur}|${id}`);
    marketErrorUntil = 0;
  }
  const { out: cached, missing } = readMarketCache(ids, cur);

  // Alles frisch im Cache → ohne Netzwerk zurück
  if (!missing.length) return cached;

  // Error-Cooldown: nicht erneut spammen, stale Cache zurückgeben
  if (Date.now() < marketErrorUntil) return cached;

  // Inflight-Dedup PRO reqKey: laufende Request für DIESELBEN IDs wiederverwenden.
  // KEIN Abbrechen fremder Requests — verschiedene Aufrufer (loadMarket bulk,
  // Picker/Markets/Review-Screens) laufen unabhängig nebeneinander.
  const reqKey = `${cur}|${[...missing].sort().join(',')}`;
  const existing = inflight.get(reqKey);
  if (existing) {
    try { await existing.promise; } catch {}
    return readMarketCache(ids, cur).out;
  }

  const controller = new AbortController(); // lokal, wird nie von außen aborted

  // Marktdaten via eigenen Serverless-Proxy (api/coin-markets.js) holen.
  // Der Proxy hängt den CoinGecko-API-Key serverseitig an und cached auf dem
  // Vercel-CDN — so werden Browser-Rate-Limits und Key-Exposition vermieden.
  const CHUNK = 250;
  const chunks = [];
  for (let i = 0; i < missing.length; i += CHUNK) {
    chunks.push(missing.slice(i, i + CHUNK));
  }

  const promise = (async () => {
    const responses = await Promise.all(chunks.map(async chunkIds => {
      const proxyUrl = `${API_BASE}/api/coin-markets?currency=${cur}&ids=${chunkIds.join(',')}&sparkline=true&pct=24h,7d`;
      const res = await fetch(proxyUrl, { signal: controller.signal });
      // Proxy nicht verfügbar (CRA-Dev-Server liefert für /api/* den SPA-index.html
      // mit Status 200 / content-type text/html — kein 404). Fallback auf direkten
      // CG-Endpoint, wenn Status 404 ODER content-type kein JSON ist.
      const ct = res.headers.get('content-type') || '';
      const proxyOk = res.ok && ct.includes('json');
      if (!proxyOk) {
        const cgUrl = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=${cur}&ids=${chunkIds.join(',')}&order=market_cap_desc&per_page=250&page=1&sparkline=true&price_change_percentage=24h,7d`;
        const cgRes = await fetch(cgUrl, { signal: controller.signal });
        if (!cgRes.ok) throw new Error(`CoinGecko ${cgRes.status}`);
        return cgRes.json();
      }
      return res.json();
    }));
    const now = Date.now();
    responses.forEach(data => {
      data.forEach(coin => {
        marketCache.set(`${cur}|${coin.id}`, {
          price: coin.current_price,
          chg:   coin.price_change_percentage_24h ?? 0,
          chg7d: coin.price_change_percentage_7d_in_currency ?? null,
          mcap:  coin.market_cap ?? null,
          image: coin.image,
          spark: coin.sparkline_in_7d?.price || null,
          fetchedAt: now,
        });
      });
    });
  })();

  inflight.set(reqKey, { promise });

  try {
    await promise;
  } catch (e) {
    if (e.name !== 'AbortError') {
      marketErrorUntil = Date.now() + MARKET_ERROR_COOLDOWN;
    }
    // Bei Fehler: gecachten + frisch geladene zurückgeben (keine leere Map)
    return readMarketCache(ids, cur).out;
  } finally {
    inflight.delete(reqKey);
  }

  return readMarketCache(ids, cur).out;
}
